import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import pensionFundsService from "@pensionFundsService";

/**
 * Hook to fetch pension plans with metadata and fund manager information
 * Filters investment accounts to show only pension plan types
 * 
 * @returns Query result containing pension plans with joined metadata
 * 
 * @example
 * const { data: pensionPlans, isLoading } = usePensionPlans();
 */
export function useFetchPensionPlans() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['pension-plans', user?.id],
    queryFn: async () => {
      return await pensionFundsService.fetchPensionPlans(user.id);
    },
    enabled: !!user,
  });
}

/**
 * Hook to create a new pension plan
 * Creates investment account and associated pension metadata in transaction
 * Handles fund manager creation if new fund manager is provided
 * 
 * @returns Mutation function to create pension plan
 * 
 * @example
 * const createPensionPlan = useCreatePensionPlan();
 * await createPensionPlan.mutateAsync(formData);
 */
export function useCreatePensionPlan() {
  const qc = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (pensionPlanData: PensionPlanFormData) => {
      const {
        fundName,
        accountNumber,
        currency,
        interestRate,
        startingAmount,
        notes,
        description,
        pensionType,
        country,
        fundManager,
        fundManagerId,
        newFundManagerName,
        employerName,
        employerContributionPercentage,
        employeeContributionPercentage
      } = pensionPlanData;

      const data: PensionPlanCreateRequest = {
        userId: user.id,
        accountNumber,
        currency,
        interestRate,
        startingAmount,
        notes,
        description,
        pensionType,
        fundName,
        fundManager,
        newFundManagerName,
        metadata: {
          country,
          pensionType,
          fundManagerId,
          employerName,
          employerContributionPercentage,
          employeeContributionPercentage,
        }
      };

      return await pensionFundsService.createPensionPlan(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pension-plans'] });
      qc.invalidateQueries({ queryKey: ['fund-managers'] });
    },
  });
}

/**
 * Hook to update an existing pension plan
 * Updates investment account and pension metadata
 * Preserves pension type and non-modified type-specific fields
 * 
 * @returns Mutation function to update pension plan
 * 
 * @example
 * const updatePensionPlan = useUpdatePensionPlan();
 * await updatePensionPlan.mutateAsync({ id: '123', fund_name: 'New Name' });
 */
export function useUpdatePensionPlan() {
  const qc = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (updateRequest: Partial<PensionPlanFormData> & { id: string }) => {
      const { id, ...updates } = updateRequest;
      return await pensionFundsService.updatePensionPlan({
        id,
        accountNumber: updates.accountNumber,
        currency: updates.currency,
        description: updates.description,
        fundManager: updates.fundManager,
        fundName: updates.fundName,
        interestRate: updates.interestRate,
        newFundManagerName: updates.newFundManagerName,
        notes: updates.notes,
        startingAmount: updates.startingAmount,
        userId: user.id,
        metadata: {
          // TODO: we need to get the metadata ID associated with the pension plan
          id: '',
          investmentAccountId: id,
          pensionType: updates.pensionType,
          country: updates.country,
          fundManagerId: updates.fundManagerId,
          employerName: updates.employerName,
          employeeContributionPercentage: updates.employeeContributionPercentage,
          employerContributionPercentage: updates.employerContributionPercentage,
        }
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pension-plans'] });
    },
  });
}

/**
 * Hook to delete a pension plan
 * Deletes investment account (CASCADE deletes metadata and transactions)
 * 
 * @returns Mutation function to delete pension plan
 * 
 * @example
 * const deletePensionPlan = useDeletePensionPlan();
 * await deletePensionPlan.mutateAsync('pension-plan-id');
 */
export function useDeletePensionPlan() {
  const qc = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (id: string) => {
      return await pensionFundsService.deletePensionPlan(id, user.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pension-plans'] });
    },
  });
}
